import { useState, useEffect } from 'react'
import {
  getCashSession,
  openCashSession,
  closeCashSession,
} from '../services/CashService'
import type { CashSession } from '../types/@types/cash'
import { useAuth } from '../context/AuthContext'

export function useCashSession() {
  const { user } = useAuth()
  const [session, setSession] = useState<CashSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function fetchSession() {
    setLoading(true)
    try {
      const data = await getCashSession()
      setSession(data)
      setError(null)
    } catch (err: any) {
      if (err.response && (err.response.status === 404 || err.response.data?.message?.includes('No se pudo obtener'))) {
        setSession(null)
        setError(null)
      } else {
        setError('No se pudo obtener la sesión de caja')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSession()
  }, [])

  async function openSession(initialAmount: number) {
    if (!user) {
      setError('Usuario no autenticado')
      return
    }
    if (initialAmount <= 0) {
      setError('Monto inválido')
      return
    }
    setOpening(true)
    try {
      await openCashSession({ user: user.id, initialAmount })
      await fetchSession()
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else {
        setError('No se pudo abrir la sesión')
      }
    } finally {
      setOpening(false)
    }
  }

  async function closeSession(
    finalAmount: number,
    observations?: string
  ): Promise<boolean> {
    if (!session?.id) {
      setError('No hay sesión abierta')
      return false
    }
    if (finalAmount < 0) {
      setError('Monto inválido')
      return false
    }
    setClosing(true)
    try {
      await closeCashSession({
        sessionId: session.id,
        finalAmount,
        observations,
      })
      await fetchSession()
      return true
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else {
        setError('No se pudo cerrar la sesión')
      }
      return false
    } finally {
      setClosing(false)
    }
  }

  return {
    session,
    loading,
    opening,
    closing,
    error,
    openSession,
    closeSession,
    reload: fetchSession,
  }
}
